import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Search, Download, Upload as UploadIcon, Copy, KeyRound } from "lucide-react";
import { ImageUpload } from "@/components/image-upload";
import { StorageAvatarImage } from "@/components/storage-image";
import { extractStoragePath } from "@/lib/storage";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/students")({
  head: () => ({ meta: [{ title: "Data Siswa — MI Al-Munajah Admin" }] }),
  component: StudentsPage,
});

type ClassRow = { id: string; name: string };
type Student = {
  id: string; nisn: string | null; full_name: string;
  class_id: string | null; gender: "L" | "P" | null;
  birth_date: string | null; address: string | null;
  parent_name: string | null; father_name: string | null; mother_name: string | null;
  parent_phone: string | null;
  email: string | null; photo_url: string | null;
};

const empty: Omit<Student, "id"> & { password?: string } = {
  nisn: "", full_name: "", class_id: null, gender: null,
  birth_date: null, address: "", parent_name: "", father_name: "", mother_name: "",
  parent_phone: "", email: "", photo_url: null, password: "",
};

function StudentsPage() {
  const [rows, setRows] = useCachedState<Student[]>("students:rows", []);
  const [classes, setClasses] = useCachedState<ClassRow[]>("students:classes", []);
  const [search, setSearch] = useState("");
  const [filterClass, setFilterClass] = useState<string>("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [form, setForm] = useState<Omit<Student, "id"> & { password?: string }>(empty);

  const load = async () => {
    const { data, error } = await supabase.from("students").select("*").order("full_name");
    if (!error && data) setRows(data as Student[]);
  };

  useEffect(() => {
    load();
    supabase.from("classes").select("id,name").order("name").then(({ data, error }) => {
      if (!error && data) setClasses(data as ClassRow[]);
    });
    const ch = supabase.channel("students-rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "students" }, load).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (s: Student) => {
    setEditing(s);
    const normalizedPhoto = extractStoragePath("avatars", s.photo_url);
    setForm({
      nisn: s.nisn, full_name: s.full_name, class_id: s.class_id, gender: s.gender,
      birth_date: s.birth_date, address: s.address, parent_name: s.parent_name,
      father_name: s.father_name, mother_name: s.mother_name,
      parent_phone: s.parent_phone, email: s.email, photo_url: normalizedPhoto,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.full_name) return toast.error("Nama wajib diisi");
    const { password, ...rest } = form;
    const payload = { ...rest, nisn: form.nisn || null };
    let studentId = editing?.id;
    if (editing) {
      const { error } = await supabase.from("students").update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
    } else {
      const { data, error } = await supabase.from("students").insert(payload).select("id").single();
      if (error) return toast.error(error.message);
      studentId = data?.id;
    }
    if (password && password.length > 0 && studentId) {
      if (password.length < 6) return toast.error("Password minimal 6 karakter");
      const { error: pErr } = await supabase.rpc("admin_set_student_password", {
        student_id: studentId, new_password: password,
      });
      if (pErr) return toast.error(`Siswa tersimpan, gagal set password: ${pErr.message}`);
    }
    toast.success(editing ? "Siswa diperbarui" : "Siswa ditambahkan");
    setOpen(false);
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("students").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Siswa dihapus");
  };


  const [pwdDialog, setPwdDialog] = useState<{ open: boolean; student: Student | null; newPassword: string; resultPassword: string; saving: boolean }>({ open: false, student: null, newPassword: "", resultPassword: "", saving: false });

  const openPwdDialog = (s: Student) => setPwdDialog({ open: true, student: s, newPassword: "", resultPassword: "", saving: false });

  const submitNewPassword = async () => {
    if (!pwdDialog.student) return;
    if (pwdDialog.newPassword.length < 6) return toast.error("Password minimal 6 karakter");
    setPwdDialog((p) => ({ ...p, saving: true }));
    const { error } = await supabase.rpc("admin_set_student_password", {
      student_id: pwdDialog.student.id, new_password: pwdDialog.newPassword,
    });
    if (error) { setPwdDialog((p) => ({ ...p, saving: false })); return toast.error(error.message); }
    setPwdDialog((p) => ({ ...p, resultPassword: p.newPassword, newPassword: "", saving: false }));
    toast.success("Password berhasil diperbarui");
  };

  const resetPasswordInDialog = async () => {
    if (!pwdDialog.student) return;
    setPwdDialog((p) => ({ ...p, saving: true }));
    const { data, error } = await supabase.rpc("admin_reset_student_password", { student_id: pwdDialog.student.id });
    if (error) { setPwdDialog((p) => ({ ...p, saving: false })); return toast.error(error.message); }
    setPwdDialog((p) => ({ ...p, resultPassword: String(data || ""), newPassword: "", saving: false }));
    toast.success("Password berhasil di-reset");
  };


  const classMap = new Map(classes.map((c) => [c.id, c.name]));
  const filtered = rows.filter((r) => {
    if (filterClass !== "all" && r.class_id !== filterClass) return false;
    if (search && !`${r.full_name} ${r.nisn || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const exportXlsx = async () => {
    const XLSX = await import("xlsx");
    const ws = XLSX.utils.json_to_sheet(filtered.map((r) => ({
      NISN: r.nisn, Nama: r.full_name, Kelas: classMap.get(r.class_id || "") || "",
      "Jenis Kelamin": r.gender === "L" ? "Laki-laki" : r.gender === "P" ? "Perempuan" : "",
      "Tanggal Lahir": r.birth_date, Alamat: r.address,
      "Nama Orang Tua": r.parent_name, "HP Orang Tua": r.parent_phone, Email: r.email,
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Siswa");
    XLSX.writeFile(wb, `siswa-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const importXlsx = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const XLSX = await import("xlsx");
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf);
    const json: Record<string, unknown>[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
    const classByName = new Map(classes.map((c) => [c.name.toLowerCase(), c.id]));
    const payload = json.map((r) => {
      const kelas = String(r.Kelas || r.kelas || "").toLowerCase();
      const gender = String(r["Jenis Kelamin"] || r.gender || "").toLowerCase();
      return {
        nisn: r.NISN ? String(r.NISN) : null,
        full_name: String(r.Nama || r.nama || "").trim(),
        class_id: classByName.get(kelas) || null,
        gender: (gender.startsWith("l") ? "L" : gender.startsWith("p") ? "P" : null) as "L" | "P" | null,
        birth_date: r["Tanggal Lahir"] ? String(r["Tanggal Lahir"]) : null,
        address: r.Alamat ? String(r.Alamat) : null,
        parent_name: r["Nama Orang Tua"] ? String(r["Nama Orang Tua"]) : null,
        parent_phone: r["HP Orang Tua"] ? String(r["HP Orang Tua"]) : null,
        email: r.Email ? String(r.Email) : null,
      };
    }).filter((p) => p.full_name);
    if (!payload.length) return toast.error("Tidak ada data");
    const { error } = await supabase.from("students").insert(payload);
    if (error) toast.error(error.message); else toast.success(`${payload.length} siswa diimport`);
    e.target.value = "";
  };

  return (
    <div>
      <PageHeader
        title="Data Siswa"
        description={`${rows.length} siswa terdaftar`}
        actions={
          <>
            <label className="inline-flex">
              <input type="file" accept=".xlsx,.xls" onChange={importXlsx} className="hidden" />
              <Button asChild variant="outline" className="rounded-xl"><span className="cursor-pointer"><UploadIcon className="w-4 h-4 mr-2" /> Import</span></Button>
            </label>
            <Button variant="outline" onClick={exportXlsx} className="rounded-xl"><Download className="w-4 h-4 mr-2" /> Export</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={startNew} className="rounded-xl"><Plus className="w-4 h-4 mr-2" /> Tambah Siswa</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl rounded-3xl">
                <DialogHeader><DialogTitle>{editing ? "Edit Siswa" : "Tambah Siswa"}</DialogTitle></DialogHeader>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="NISN"><Input value={form.nisn || ""} onChange={(e) => setForm({ ...form, nisn: e.target.value })} /></Field>
                  <Field label="Nama Lengkap *"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
                  <Field label="Kelas">
                    <Select value={form.class_id || ""} onValueChange={(v) => setForm({ ...form, class_id: v || null })}>
                      <SelectTrigger><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                      <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Jenis Kelamin">
                    <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: (v || null) as "L" | "P" | null })}>
                      <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L">Laki-laki</SelectItem>
                        <SelectItem value="P">Perempuan</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Tanggal Lahir"><Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value || null })} /></Field>
                  <Field label="Email"><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                  
                  <Field label="Nama Ayah"><Input value={form.father_name || ""} onChange={(e) => setForm({ ...form, father_name: e.target.value })} /></Field>
                  <Field label="Nama Ibu"><Input value={form.mother_name || ""} onChange={(e) => setForm({ ...form, mother_name: e.target.value })} /></Field>
                  <Field label="HP Orang Tua"><Input value={form.parent_phone || ""} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} /></Field>
                  <Field label="Alamat" className="sm:col-span-2"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                  <Field label={editing ? "Password Baru (kosongkan jika tidak diubah)" : "Password Login *"} className="sm:col-span-2">
                    <Input type="text" autoComplete="new-password" placeholder={editing ? "Kosongkan jika tidak ingin mengganti" : "min 6 karakter"} value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  </Field>
                  <Field label="Foto Profil" className="sm:col-span-2">
                    <ImageUpload bucket="avatars" storePathOnly value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} />
                  </Field>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
                  <Button onClick={save} className="rounded-xl">{editing ? "Simpan" : "Tambah"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <Card className="p-4 rounded-2xl mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama atau NISN..." className="pl-9 rounded-xl" />
          </div>
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="sm:w-48 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua kelas</SelectItem>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Mobile: compact card list */}
      <div className="sm:hidden space-y-2">
        {filtered.length === 0 ? (
          <Card className="rounded-2xl p-6 text-center text-sm text-muted-foreground">Belum ada data</Card>
        ) : filtered.map((s) => (
          <Card key={s.id} className="rounded-2xl p-3">
            <div className="flex items-start gap-3">
              <Avatar className="w-11 h-11 shrink-0">
                {s.photo_url && <StorageAvatarImage bucket="avatars" value={s.photo_url} />}
                <AvatarFallback className="bg-primary/15 text-primary text-xs">{s.full_name.split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="font-semibold leading-tight truncate">{s.full_name}</div>
                <div className="text-xs text-muted-foreground truncate">{s.email || "-"}</div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
                  <Badge variant="secondary" className="rounded-full font-mono">{s.nisn || "-"}</Badge>
                  <Badge variant="outline" className="rounded-full">Kelas {classMap.get(s.class_id || "") || "Tanpa kelas"}</Badge>
                  <Badge variant="outline" className="rounded-full ml-auto">{s.gender === "L" ? "Laki-laki" : s.gender === "P" ? "Perempuan" : "-"}</Badge>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  <div className="truncate">Ortu: {s.parent_name || s.father_name || s.mother_name || "-"}</div>
                  <div className="truncate">HP: {s.parent_phone || "-"}</div>
                </div>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t flex justify-end gap-1">
              <Button variant="ghost" size="icon" className="h-9 w-9" title="Password" onClick={() => openPwdDialog(s)}><KeyRound className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => startEdit(s)}><Pencil className="w-4 h-4" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-9 w-9"><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Hapus siswa?</AlertDialogTitle>
                    <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Batal</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(s.id)}>Hapus</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
      </div>

      {/* Desktop: full table */}
      <Card className="rounded-2xl overflow-hidden hidden sm:block">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Siswa</TableHead>
                <TableHead>NISN</TableHead>
                <TableHead>Kelas</TableHead>
                <TableHead>JK</TableHead>
                <TableHead>Orang Tua</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Belum ada data</TableCell></TableRow>
              ) : filtered.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9">
                        {s.photo_url && <StorageAvatarImage bucket="avatars" value={s.photo_url} />}
                        <AvatarFallback className="bg-primary/15 text-primary text-xs">{s.full_name.split(" ").map(x=>x[0]).slice(0,2).join("")}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{s.full_name}</div>
                        <div className="text-xs text-muted-foreground">{s.email || "-"}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{s.nisn || "-"}</TableCell>
                  <TableCell>{classMap.get(s.class_id || "") || "-"}</TableCell>
                  <TableCell><Badge variant="secondary" className="rounded-full">{s.gender || "-"}</Badge></TableCell>
                  <TableCell>
                    <div className="text-sm">{s.parent_name || "-"}</div>
                    <div className="text-xs text-muted-foreground">{s.parent_phone || "-"}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" title="Ganti / Reset Password Login" onClick={() => openPwdDialog(s)}><KeyRound className="w-4 h-4" /></Button>
                    
                    <Button variant="ghost" size="icon" onClick={() => startEdit(s)}><Pencil className="w-4 h-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon"><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Hapus siswa?</AlertDialogTitle>
                          <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Batal</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(s.id)}>Hapus</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>



      <Dialog open={pwdDialog.open} onOpenChange={(o) => !o && setPwdDialog({ open: false, student: null, newPassword: "", resultPassword: "", saving: false })}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle>{pwdDialog.resultPassword ? "Password Berhasil Diperbarui" : "Ganti Password Siswa"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm p-3 rounded-xl bg-muted/50">
              <div><span className="text-muted-foreground">NISN:</span> <span className="font-mono">{pwdDialog.student?.nisn || "-"}</span></div>
              <div><span className="text-muted-foreground">Nama:</span> <span className="font-medium">{pwdDialog.student?.full_name}</span></div>
            </div>
            {pwdDialog.resultPassword ? (
              <div className="p-4 rounded-xl bg-muted text-center">
                <div className="text-xs text-muted-foreground mb-1">Password Baru (catat / bagikan ke siswa)</div>
                <div className="text-2xl font-mono font-bold tracking-widest break-all">{pwdDialog.resultPassword}</div>
                <Button variant="outline" size="sm" className="mt-2 rounded-xl" onClick={() => { navigator.clipboard.writeText(pwdDialog.resultPassword); toast.success("Password disalin"); }}>
                  <Copy className="w-3 h-3 mr-1" /> Salin
                </Button>
                <p className="text-[11px] text-muted-foreground mt-2">Password tidak dapat dilihat kembali setelah dialog ditutup.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Password Baru (min 6 karakter)</Label>
                <Input type="text" autoComplete="new-password" placeholder="Ketik password baru..." value={pwdDialog.newPassword} onChange={(e) => setPwdDialog((p) => ({ ...p, newPassword: e.target.value }))} />
                <p className="text-[11px] text-muted-foreground">Password lama tidak dapat ditampilkan (tersimpan sebagai bcrypt hash).</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            {pwdDialog.resultPassword ? (
              <Button onClick={() => setPwdDialog({ open: false, student: null, newPassword: "", resultPassword: "", saving: false })} className="rounded-xl">Selesai</Button>
            ) : (
              <Button disabled={pwdDialog.saving} onClick={submitNewPassword} className="rounded-xl">Simpan</Button>

            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={`space-y-1.5 ${className || ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
